import React from 'react';
import { InputNumber, Select, Button, Divider, Checkbox } from 'antd';
import ConfigurationLayout from '../configuration.layout';
import styles from './styles/limits-step.module.scss';

const { Option } = Select;

function LimitsStep({
    state,
    firstTimeSetup,
    setMaxAdvances,
    setAdvanceTimeframe,
    setMinAdvanceAmount,
    setMaxAdvanceAmount,
    setMaxAdvanceVolume,
    toggleLimitsSection,
    getLimitsSummary,
    hasChanges,
    onSubmit,
    onContinue,
    onCancel,
    onStepChange
}) {
    const { limit_policy, limits_enabled = {}, loading, user } = state;
    const { maxAdvances = 12, advanceTimeframe = 'pay-period', minAdvanceAmount = 5, maxAdvanceAmount = 700, maxAdvanceVolume = 1500 } = limit_policy || {};
    const { maxAdvances: enableMaxAdvances = true, advanceAmounts: enableAdvanceAmounts = true, totalVolume: enableTotalVolume = true } = limits_enabled;
    const summary = getLimitsSummary();

    // Main content - form section
    const mainContent = (
        <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Configure your limits & guardrails</h2>

            <div className={styles.formFields}>
                {/* Number of Advances in a Timeframe */}
                <div className={`${styles.sectionCard} ${!enableMaxAdvances ? styles.sectionDisabled : ''}`}>
                    <div className={styles.sectionHeader}>
                        <Checkbox checked={enableMaxAdvances} onChange={() => toggleLimitsSection('maxAdvances')}>
                            <span className={styles.fieldLabel}>Number of Advance in a Timeframe</span>
                        </Checkbox>
                        <p className={styles.fieldDescription}>The maximum number of advances a user can make in your selected timeframe</p>
                    </div>
                    <div className={styles.advanceInputGroup}>
                        <InputNumber
                            value={maxAdvances}
                            onChange={setMaxAdvances}
                            min={0}
                            max={999}
                            className={styles.advanceInput}
                            disabled={!enableMaxAdvances}
                        />
                        <span className={styles.advanceText}>advances per</span>
                        <Select value={advanceTimeframe} onChange={setAdvanceTimeframe} className={styles.timeframeSelect} disabled={!enableMaxAdvances}>
                            <Option value="pay-period">Pay period</Option>
                            <Option value="month">Month</Option>
                            <Option value="week">Week</Option>
                            <Option value="day">Day</Option>
                        </Select>
                    </div>
                </div>

                {/* Max and Min Volume Per Advance */}
                <div className={`${styles.sectionCard} ${!enableAdvanceAmounts ? styles.sectionDisabled : ''}`}>
                    <div className={styles.sectionHeader}>
                        <Checkbox checked={enableAdvanceAmounts} onChange={() => toggleLimitsSection('advanceAmounts')}>
                            <span className={styles.fieldLabel}>Max and Min Volume Per Advance</span>
                        </Checkbox>
                        <p className={styles.fieldDescription}>The minimum and maximum volume a user can make per advance</p>
                    </div>
                    <div className={styles.volumeInputGroup}>
                        <InputNumber
                            value={minAdvanceAmount}
                            onChange={setMinAdvanceAmount}
                            min={1}
                            max={9999}
                            className={styles.volumeInput}
                            prefix="$"
                            disabled={!enableAdvanceAmounts}
                        />
                        <span className={styles.volumeLabel}>minimum</span>
                        <InputNumber
                            value={maxAdvanceAmount}
                            onChange={setMaxAdvanceAmount}
                            min={1}
                            max={99999}
                            className={styles.volumeInput}
                            prefix="$"
                            disabled={!enableAdvanceAmounts}
                        />
                        <span className={styles.volumeLabel}>maximum</span>
                    </div>
                </div>

                {/* Advance Volume Per Pay Period */}
                <div className={`${styles.sectionCard} ${!enableTotalVolume ? styles.sectionDisabled : ''}`}>
                    <div className={styles.sectionHeader}>
                        <Checkbox checked={enableTotalVolume} onChange={() => toggleLimitsSection('totalVolume')}>
                            <span className={styles.fieldLabel}>Advance Volume Per Pay Period</span>
                        </Checkbox>
                        <p className={styles.fieldDescription}>The maximum advance volume a user can make per pay period</p>
                    </div>
                    <div className={styles.volumeInputGroup}>
                        <InputNumber
                            value={maxAdvanceVolume}
                            onChange={setMaxAdvanceVolume}
                            min={1}
                            max={999999}
                            className={styles.volumeInput}
                            prefix="$"
                            disabled={!enableTotalVolume}
                        />
                        <span className={styles.volumeLabel}>maximum</span>
                    </div>
                </div>
            </div>
        </div>
    );

    // Side content - summary section
    const sideContent = (
        <div className={styles.sideContent}>
            <div className={styles.userProfile}>
                <div className={styles.userAvatar}>
                    <img src="/user.png" alt="User" className={styles.avatarImage} />
                </div>
                <div className={styles.userName}>Meet Michael,</div>
            </div>

            <h3 className={styles.summaryTitle}>Limits Summary</h3>

            <div className={styles.summarySection}>
                {summary.map((item, index) => (
                    <div key={index} className={styles.summaryBlock}>
                        <span className={styles.summaryText}>{item}</span>
                    </div>
                ))}
            </div>

            {!firstTimeSetup && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    {hasChanges() && (
                        <div className={styles.actionButtons}>
                            <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="primary" size="large" loading={loading} onClick={onSubmit} className={styles.continueButton}>
                                Submit
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {firstTimeSetup && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    <div className={styles.actionButtons}>
                        <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" size="large" loading={loading} onClick={onSubmit} className={styles.continueButton}>
                            Submit All
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <ConfigurationLayout isFirstTimeSetUp={firstTimeSetup} currentStep={3} sideContent={sideContent} onStepChange={onStepChange} user={user}>
            {mainContent}
        </ConfigurationLayout>
    );
}

export default LimitsStep;
